import React, {useEffect, useState} from 'react';

import './Team.create.scss';
import Button from "../../forms/buttons/Button";
import Input from "../../forms/inputs/Input";
import {useNavigate, useParams} from "react-router-dom";
import FormValidation from "../../forms/validation/formValidation";
import TextArea from "../../forms/inputs/TextArea";
import {useLazyQuery, useMutation} from "@apollo/client";
import gqlSchemaLibrary from "../../services/graphql.schemas";
import {clearGqlTypename} from "../../utils/clearTypename";
import {useTranslation} from "react-i18next";
import {PageViewLog} from "../../services/interactions/page.view";
import AuditTrail from "../../services/interactions/audit.trail";

const fields = {
    'name': { type: 'text', required: true, value: ''},
    'description': { type: 'text', required: false, value: '' }
};

function TeamCreate({ event, currentOrg, currentUser, match }) {
    let { teamId, programID } = useParams();
    const {t} = useTranslation(['common','teams']);
    const navigate = useNavigate();
    const [__submitting, __setSubmitting] = useState(false);
    const [isEditting, setIsEditting] = useState(false);

    const [dataFields, setDataFields] = useState(fields);
    const [formValid, setFormValid] = useState(false);
    const [team, setTeam] = useState(null);
    const [teamData, setTeamData] = useState({});

    // UI Element
    const parentRef = React.createRef();
    const [toTop, setToTop] = useState(false);

    const manageScrollToBottom = () => {
        const scrollPos = parentRef.current.scrollTop;
        if(scrollPos > 80) {
                setToTop(true);
            } else {
                setToTop(false);
            }
    }

    const [createTeam] = useMutation(gqlSchemaLibrary.teams.mutation.createTeam, {
        onCompleted: ({ createTeam }) => {
            console.log('team created: ', createTeam);
            navigate(`/teams/manage/${createTeam._id}`);
        },
        onError: (e) => {
            console.error('Create Team Error: ', e);
            __setSubmitting(false);
        }
    });

    const [editTeam] = useMutation(gqlSchemaLibrary.teams.mutation.editTeam, {
        onCompleted: ({ editTeam }) => {
            navigate(`/teams/detail/${team._id}`);
        },
        onError: (e) => {
            console.error('Create Team Error: ', e);
            __setSubmitting(false);
        }
    });

    const [_getTeam] = useLazyQuery(gqlSchemaLibrary.teams.queries.getTeam, {
        onCompleted: ({ getTeam }) => {
            if (getTeam) {
                console.log(clearGqlTypename(getTeam));
                setTeam(clearGqlTypename(getTeam));
            } else {
                navigate(`/teams`);
            }
        },
        onError: (e) => {
            navigate(`/teams`);
        }
    });

    useEffect(() => {
        const onFormChange = (key, value, field, valid) => {
            let p = teamData;
            p[key] = value;
            setTeamData(p);
            setDataFields(prevState => ({
                ...prevState,
                [key]: field
            }));
            setFormValid(valid);
        };
        let form = FormValidation(fields, onFormChange);
        if (teamId && !!team) {
            form.setValue('name', team.name);
            form.setValue('description', team.description? team.description : '');
        }
        setDataFields(form.data);
    }, [fields, team, match])

    useEffect(() => {
        if (teamId) {
            setIsEditting(true);
            _getTeam({variables: {
                teamId: teamId
            }});
        }
    }, [match, _getTeam])

    const cancel = () => {
        AuditTrail.sendLog(event, {tag: 'button', event: 'onClick', action: 'button back create team'});
        navigate(`/teams`);
    }

    const validateAndSubmit = () => {
        if (!currentUser.userRoles.userClinicians.edit  && teamId) {
            return;
        } else if (!currentUser.userRoles.userClinicians.create) {
            return;
        }
        if (!formValid || __submitting) {
            return; // Not valid yet or submitting
        }
        __setSubmitting(true);
        if (isEditting || programID) {
            if (!team) {
                console.error('No team!');
                __setSubmitting(false);
                return;
            }
            AuditTrail.sendLog(event, {tag: 'button', event: 'onClick', action: 'button edit team', team_id: team._id, team: teamData});
            teamData._id = team._id;
            editTeam({
                variables: {
                    team: teamData
                }
            });
        } else {
            AuditTrail.sendLog(event, {tag: 'button', event: 'onClick', action: 'button create new team', team: teamData});
            createTeam({
                variables: {
                    team: teamData
                }
            });
        }
    }

    return (
        <div id="TeamCreate">
            <div className="headerTitle">
                <div>
                </div>
                <div className="holder">
                    <h2>{dataFields['name'].value}</h2>
                </div>
                <Button className="tertiary white" handleClick={cancel}>
                    <span>{t('cancel')}</span>
                </Button>
                <Button disabled={!formValid} className="primary green smallPad" handleClick={validateAndSubmit}>
                    <div className={`progressButton ${__submitting ? 'submitting' : ''}`}>
                        {__submitting ? <div className="_progressing"></div> : ''}
                        <span>{teamId ? t('done') : t('teams:team-create') }</span>
                    </div>
                </Button>
            </div>
            <div className="teamSection scrollBar" ref={parentRef} onScroll={manageScrollToBottom}>
                <div className="subSections">
                </div>
                <div className="formContent">
                    <h3>{t('teams:team-information')}</h3>
                    <div className="row two">
                        <Input required={dataFields['name'].required} error={dataFields['name'].error} value={dataFields['name'].value}
                               label={t('name')} onChange={dataFields['name'].onChange} event={event}/>
                    </div>
                    <div className="row two">
                        <TextArea required={dataFields['description'].required} value={dataFields['description'].value}
                                  label={t('description')} onChange={dataFields['description'].onChange} event={event}/>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default PageViewLog(TeamCreate);
